import React, { useState, useEffect, useCallback, useMemo } from 'react';

import { Select, type SelectProps } from 'antd';
import { useTranslation } from 'react-i18next';

interface Currency {
  code: string;
  symbol: string;
  name_key: string;
  decimal_places?: number;
  [key: string]: unknown;
}

interface CurrencySelectProps extends Omit<SelectProps<string>, 'options' | 'onChange' | 'onSearch' | 'onSelect' | 'onBlur' | 'value' | 'showSearch' | 'filterOption' | 'loading'> {
  currencies?: Currency[];
  apiFetch?: (url: string) => Promise<Response>;
  value?: string;
  onChange?: (value: string) => void;
}

/**
 * CurrencySelect - 共用貨幣選擇元件
 *
 * 支援從下拉清單選擇，也允許直接輸入不在清單中的貨幣代碼。
 */
const CurrencySelect: React.FC<CurrencySelectProps> = ({ currencies: currenciesProp, apiFetch, value, onChange, ...rest }) => {
  const { t } = useTranslation();
  const [fetchedCurrencies, setFetchedCurrencies] = useState<Currency[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const currencies = currenciesProp || fetchedCurrencies;

  const fetchCurrencies = useCallback(async () => {
    if (!apiFetch) return;
    setLoading(true);
    try {
      const response = await apiFetch('currencies');
      const result = await response.json();
      if (result.success && result.data) {
        setFetchedCurrencies(result.data);
      }
    } catch (error) {
      console.error('Failed to fetch currencies:', error);
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    if (!currenciesProp && apiFetch) {
      fetchCurrencies();
    }
  }, [currenciesProp, apiFetch, fetchCurrencies]);

  const knownCodes = useMemo(() => new Set(currencies.map((c) => c.code)), [currencies]);

  const options = useMemo(() => {
    const base = currencies.map((c) => ({
      value: c.code,
      label: `${c.code} (${c.symbol}) - ${t(c.name_key)}`,
    }));

    // 搜尋文字不在清單中時，動態插入自訂選項讓使用者可以選取
    const trimmed = searchValue.toUpperCase().trim();
    if (trimmed && !knownCodes.has(trimmed)) {
      base.unshift({ value: trimmed, label: `${trimmed} (${t('shared.currencies.custom', 'Custom')})` });
    }

    return base;
  }, [currencies, searchValue, knownCodes, t]);

  const handleSelect = (val: string) => {
    setSearchValue('');
    onChange?.(val);
  };

  return (
    <Select
      showSearch
      value={value}
      options={options}
      onSearch={setSearchValue}
      onSelect={handleSelect}
      onBlur={() => setSearchValue('')}
      filterOption={(input, option) =>
        String(option?.label ?? '').toUpperCase().includes(input.toUpperCase())
      }
      loading={loading}
      {...rest}
    />
  );
};

export default CurrencySelect;
